import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Paginator as UntypedPaginator } from '../../components/Paginator/Paginator';
import { Loader } from '../../components/Loader/Loader';
import {
  artifactsApi,
  ArtifactArchived,
  ArtifactBundleListDTO,
  ArtifactSorting,
  ArtifactType,
} from '../../utils/artifactsApi';
import { getIsSuperAdmin, getWorkspaceRoles } from '../../utils/artifactsUi';
import { showRequestError } from '../../utils/requestError';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { BundleCard } from './BundleCard';
import { ArtifactFiltersBar } from './ArtifactFiltersBar';
import { PublishBundleModal } from './PublishBundleModal';
import styles from './ArtifactsPage.module.scss';

const Paginator = UntypedPaginator as unknown as React.FC<{
  size: number;
  page: number;
  total: number;
  onPageChange: (page: number) => void;
}>;

const PAGE_SIZE = 24;

export function ArtifactsPage() {
  const { t } = useTranslation();
  const [bundles, setBundles] = useState<ArtifactBundleListDTO | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [publishOpen, setPublishOpen] = useState(false);
  const navigate = useNavigate();

  // roles gate both the route and the fetch: a role-less 403 would wipe the
  // token and force a logout (API.fetch behavior)
  const [currentUser] = useCurrentUser();
  const workspaceRoles = getWorkspaceRoles(currentUser);
  const hasReadRole = Boolean(workspaceRoles?.includes('artifacts:read'));
  const isSuperAdmin = getIsSuperAdmin(currentUser);
  const canSeeRegistry = Boolean(isSuperAdmin && workspaceRoles?.includes('artifacts:read'));
  // writes are super-admin-only on top of the publish role (backend _writer_deps)
  const canPublish = Boolean(isSuperAdmin && workspaceRoles?.includes('artifacts:publish'));

  const requestedPage = Number(searchParams.get('page') ?? 1);
  const page = Number.isInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const q = searchParams.get('q') ?? '';
  const typeParams = searchParams.getAll('type') as ArtifactType[];
  const sortingParam = (searchParams.get('sorting') as ArtifactSorting) || '-published_at';
  const archivedParam = (searchParams.get('archived') as ArtifactArchived) || 'hide';
  const tags = searchParams.getAll('tags');
  const dateFrom = searchParams.get('date_from') ?? '';
  const dateTo = searchParams.get('date_to') ?? '';

  const fetchBundles = useCallback(
    (signal?: AbortSignal) => {
      if (!hasReadRole) return;
      setBundles(null);
      artifactsApi
        .listBundles(
          {
            q: q || undefined,
            types: typeParams.length > 0 ? typeParams : undefined,
            tags: tags.length > 0 ? tags : undefined,
            dateFrom: dateFrom || undefined,
            dateTo: dateTo || undefined,
            archived: archivedParam,
            sorting: sortingParam,
            page,
            pageSize: PAGE_SIZE,
          },
          signal,
        )
        .then((data) => setBundles(data))
        .catch((error) => showRequestError(error, t('Artifacts')));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasReadRole, q, typeParams.join(' '), sortingParam, archivedParam, page, dateFrom, dateTo, tags.join(' '), t],
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchBundles(controller.signal);
    return () => controller.abort();
  }, [fetchBundles]);

  if (currentUser && !hasReadRole) return <Navigate to="/" replace />;

  const setParam = (name: string, value: string) => {
    setSearchParams((params) => {
      if (value) params.set(name, value);
      else params.delete(name);
      // changing a filter resets to the first page; paging itself must not
      if (name !== 'page') params.delete('page');
      return params;
    });
  };

  const setTypes = (values: ArtifactType[]) => {
    setSearchParams((params) => {
      params.delete('type');
      values.forEach((value) => params.append('type', value));
      params.delete('page');
      return params;
    });
  };

  const setDates = (from: string, to: string) => {
    setSearchParams((params) => {
      if (from) params.set('date_from', from);
      else params.delete('date_from');
      if (to) params.set('date_to', to);
      else params.delete('date_to');
      params.delete('page');
      return params;
    });
  };

  const addTag = (tag: string) => {
    setSearchParams((params) => {
      if (!params.getAll('tags').includes(tag)) params.append('tags', tag);
      params.delete('page');
      return params;
    });
  };

  const removeTag = (tag: string) => {
    setSearchParams((params) => {
      const kept = params.getAll('tags').filter((existing) => existing !== tag);
      params.delete('tags');
      kept.forEach((existing) => params.append('tags', existing));
      params.delete('page');
      return params;
    });
  };

  return (
    <div className="list-content">
      <div className="page-header">
        <div>
          <div className={`breadcrumb ${styles.crumbs}`}>
            <span>{t('Artifacts')}</span>
          </div>
          <h1 className={styles.pageTitle}>{t('Artifacts')}</h1>
        </div>
        <div className={styles.headerActions}>
          {canPublish && (
            <button type="button" className={styles.publishButton} onClick={() => setPublishOpen(true)}>
              {t('Publish')}
            </button>
          )}
          {canSeeRegistry && (
            <Link to="/artifacts/registry" className={styles.registryButton}>
              {t('Registry')}
            </Link>
          )}
        </div>
      </div>

      {canPublish && (
        <PublishBundleModal
          isVisible={publishOpen}
          onClose={() => setPublishOpen(false)}
          onPublished={(bundle) => {
            setPublishOpen(false);
            navigate(`/artifacts/${bundle.id}`);
          }}
        />
      )}

      <ArtifactFiltersBar
        q={q}
        types={typeParams}
        dateFrom={dateFrom}
        dateTo={dateTo}
        sorting={sortingParam}
        archived={archivedParam}
        showArchivedFilter={isSuperAdmin}
        onSearch={(value) => setParam('q', value)}
        onTypes={setTypes}
        onDates={setDates}
        onSorting={(value) => setParam('sorting', value)}
        onArchived={(value) => setParam('archived', value === 'hide' ? '' : value)}
      />

      {tags.length > 0 && (
        <div className={styles.activeTags}>
          {tags.map((tag) => (
            <button
              key={tag}
              type="button"
              className={styles.activeTag}
              onClick={() => removeTag(tag)}
            >
              {tag} ✕
            </button>
          ))}
        </div>
      )}

      {bundles === null && <Loader />}

      {bundles !== null && bundles.objects.length === 0 && (
        <div className="noData">{t('No artifacts yet')}</div>
      )}

      {bundles !== null && bundles.objects.length > 0 && (
        <>
          <div className={styles.grid}>
            {bundles.objects.map((bundle) => (
              <BundleCard
                key={bundle.id}
                bundle={bundle}
                to={`/artifacts/${bundle.id}`}
                onTagClick={addTag}
              />
            ))}
          </div>
          <Paginator
            size={PAGE_SIZE}
            page={page}
            total={bundles.total}
            onPageChange={(newPage) => setParam('page', newPage > 1 ? String(newPage) : '')}
          />
        </>
      )}
    </div>
  );
}
